import { useQuery } from "@apollo/client";
import PersonAdd from "@mui/icons-material/PersonAdd";
import { Alert, Box, Skeleton, Stack, Button, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { FullScreenDocumentPreview } from "components/Document/shared";
import {
  DocumentUploadSection,
  type SubmissionDocument,
} from "components/Submission/DocumentUploadSection";
import { EmbeddedDocumentSigning } from "components/Submission/EmbeddedSignature/EmbeddedDocumentSigning";
import {
  partitionReviewersAndSigners,
  sortSubmissionDocumentsByType,
  toSubmissionDocumentDisplay as toDisplayDocument,
} from "components/Submission/helpers/documentHelpers";
import { SignatureConfirmationAlert } from "components/Submission/SignatureConfirmationAlert";
import { SignaturePreparationBanner } from "components/Submission/SignaturePreparationBanner";
import { SubmissionDisplayReviewOrSignDocument } from "components/Submission/SubmissionDisplayReviewOrSignDocument";
import { SubmissionDocumentPreview } from "components/Submission/SubmissionDocumentPreview";
import { SubmissionReadOnlySummary } from "components/Submission/SubmissionReadOnlySummary";
import { SubmissionRevisionBanner } from "components/Submission/SubmissionRevisionBanner";
import { SubmissionStatusChipWithPopover } from "components/Submission/SubmissionStatusChipWithPopover";
import { UnfinishedChangesPrompt } from "components/UnfinishedChangesPrompt";
import {
  SignatureStatus,
  SignatureType,
  SubmissionDocumentType,
  SubmissionStatus,
  SubmissionStepType,
} from "generated-graphql/graphql";
import {
  computeSubmissionReviewStates,
  SubmissionReviewState,
} from "hooks/submission/reviewStates";
import { useSignatureConfirmation } from "hooks/submission/useSignatureConfirmation";
import { useSubmissionDocumentPreview } from "hooks/submission/useSubmissionDocumentPreview";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useStepperNavigation } from "hooks/useStepperNavigation";
import {
  useApproveSubmissionDocumentReview,
  useAssignPeerReviewToMe,
  useRejectSubmissionDocumentReview,
} from "hooks/useSubmissionMutations";
import { useWasteStepper } from "hooks/useWasteStepper";
import { GET_EMBEDDED_DOCUMENT_SIGNING_URL } from "queries/submissions";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { pickTransitionErrorMessage } from "routes/Customer/Waste/Submissions/ReadOnly/pickTransitionErrorMessage";

import { DetailsCard } from "../DetailsCard";
import { StepHeader } from "../StepHeader";
import { DocumentStepStatusBanner } from "./DocumentStepStatusBanner";

type ReviewFormValues = { reviewFinished: boolean; rejectionComment: string };

/**
 * Review & sign step: reviewers and signers act on their part here, gated by
 * the review state, once Reviewer Preparation has sent the document for
 * review. Also the read-only "View Submission" display for staff.
 */
export function ReviewAndSignStep({
  submissionId,
  facilityId,
}: {
  submissionId: string;
  facilityId: string;
}) {
  const alerts = useAlerts();
  const { tenantId = "" } = useParams();
  const { isStaff, user } = useCurrentUser();
  const { submission, refetchSubmission, isRejected } = useWasteStepper();
  const { goToPreviousStep } = useStepperNavigation();

  const [approveDocumentReview, { loading: approving }] = useApproveSubmissionDocumentReview();
  const [rejectDocumentReview, { loading: rejecting }] = useRejectSubmissionDocumentReview();
  const [assignPeerReviewToMe, { loading: claiming }] = useAssignPeerReviewToMe();

  const status = submission?.status ?? SubmissionStatus.InProgress;
  const persistedReviewers = useMemo(() => submission?.reviewers ?? [], [submission]);
  const documents = useMemo(() => submission?.documents ?? [], [submission]);

  const {
    signingCompleted,
    signingFlowConfirmed,
    signingConfirmationPending,
    signatureConfirmationState,
    isAwaitingPostSignatureTransition,
    markSignatureSubmitted,
    retrySignatureConfirmation,
  } = useSignatureConfirmation({
    currentUserId: user?.id,
    reviewers: persistedReviewers,
    submissionStatus: status,
    refetchSubmission,
  });

  const [signingDialogOpen, setSigningDialogOpen] = useState(false);
  const { control, handleSubmit, watch, formState } = useForm<ReviewFormValues>({
    defaultValues: { reviewFinished: true, rejectionComment: "" },
  });
  const reviewFinished = watch("reviewFinished");
  const rejectionComment = watch("rejectionComment");

  // --- Review-state gating ---------------------------------------------------
  const { reviewState, isReviewerASigner, currentUsersSubmissionDocumentReviewId } = useMemo(
    () => computeSubmissionReviewStates({ currentUser: user, submissionData: { submission } }),
    [user, submission]
  );

  // --- Document open link --------------------------------------------------
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const selectedDocument = useMemo(() => {
    const explicit = selectedDocumentId
      ? documents.find((d) => d.document.id === selectedDocumentId)
      : undefined;
    return (
      explicit ??
      documents.find((d) => d.type === SubmissionDocumentType.Submission) ??
      documents.find((d) => d.type === SubmissionDocumentType.Attachment) ??
      null
    );
  }, [documents, selectedDocumentId]);
  const persistedSignerForSigning = persistedReviewers.find((reviewer) => reviewer.isSigner);
  const isSelectedDocumentPreparedForSigning =
    selectedDocument?.type === SubmissionDocumentType.Submission &&
    selectedDocument.externalDocumentId != null &&
    (persistedSignerForSigning?.signatureStatus === SignatureStatus.Sent ||
      persistedSignerForSigning?.signatureStatus === SignatureStatus.Completed);
  const selectedDocumentPreviewId = selectedDocument?.document.id ?? null;
  const handleDocumentPreviewError = useCallback(
    (error: unknown) => {
      alerts.error("Unable to preview this document.", error);
    },
    [alerts]
  );
  const { documentUrl, hasError: documentPreviewFailed } = useSubmissionDocumentPreview({
    documentId: selectedDocumentPreviewId,
    inline: true,
    onError: handleDocumentPreviewError,
  });

  // Full-screen preview is independent of the inline selection above, so a row
  // can be opened full-page without first becoming the inline-previewed document.
  const [getDocumentDownloadLink] = useDocumentDownloadLink();
  const [fullScreenDocument, setFullScreenDocument] = useState<SubmissionDocument | null>(null);
  const [fullScreenUrl, setFullScreenUrl] = useState<string | null>(null);
  const [fullScreenPreviewOpen, setFullScreenPreviewOpen] = useState(false);
  const handleOpenFullScreen = useCallback(
    async (doc: SubmissionDocument) => {
      try {
        const { data } = await getDocumentDownloadLink({ variables: { id: doc.id, inline: true } });
        if (!data?.getDocumentDownloadLink) {
          throw new Error("No download link returned");
        }
        setFullScreenDocument(doc);
        setFullScreenUrl(data.getDocumentDownloadLink);
        setFullScreenPreviewOpen(true);
      } catch (error) {
        alerts.error("Unable to preview this document.", error);
      }
    },
    [getDocumentDownloadLink, alerts]
  );

  const { data: signingUrlData } = useQuery(GET_EMBEDDED_DOCUMENT_SIGNING_URL, {
    variables: { submissionId },
    fetchPolicy: "network-only",
    skip:
      status !== SubmissionStatus.InReview ||
      reviewState !== SubmissionReviewState.SIGNER_CAN_SIGN ||
      !isSelectedDocumentPreparedForSigning,
  });

  const handleReviewSubmit = useCallback(
    async (values: ReviewFormValues) => {
      if (!currentUsersSubmissionDocumentReviewId) {
        alerts.error("Unable to locate a document review to update.");
        return;
      }
      try {
        if (values.reviewFinished) {
          await approveDocumentReview({
            variables: { submissionDocumentReviewId: currentUsersSubmissionDocumentReviewId },
          });
          alerts.success("Review completed. Thank you!");
        } else {
          await rejectDocumentReview({
            variables: {
              submissionDocumentReviewId: currentUsersSubmissionDocumentReviewId,
              rejectionComment: values.rejectionComment.trim(),
            },
          });
          alerts.success("Sent back to Encamp for edits.");
        }
      } catch (error) {
        const safeMessage = pickTransitionErrorMessage(error);
        if (safeMessage) {
          alerts.warning(`Your review was saved. ${safeMessage}`);
        } else {
          alerts.error("Unable to complete review. Please try again.", error);
        }
      }
    },
    [currentUsersSubmissionDocumentReviewId, approveDocumentReview, rejectDocumentReview, alerts]
  );

  const handleClaimPeerReview = useCallback(async () => {
    try {
      await assignPeerReviewToMe({ variables: { submissionId } });
      alerts.success("You're now assigned as the peer reviewer.");
    } catch (error) {
      alerts.error("Failed to assign peer review to you.", error);
    }
  }, [assignPeerReviewToMe, submissionId, alerts]);

  // --- Derived display flags ----------------------------------------------
  // Any staff reviewer withdraws the offer, or a second staff user could claim a
  // peer review someone else already owns and add a duplicate reviewer row.
  const canClaimPeerReview =
    isStaff &&
    submission?.peerReviewRequired === true &&
    status === SubmissionStatus.EncampInitialReview &&
    !persistedReviewers.some((r) => r.isStaff);

  // Nothing is under review until the submission has been sent. `reviewState`
  // alone doesn't say this — REVIEWER_CAN_REVIEW falls out of being on the
  // reviewer list with no turn taken, so it's already true while staff are
  // still preparing the config on the Reviewer Preparation step. Gate on the
  // status instead.
  const isInReviewPhase =
    status === SubmissionStatus.EncampInitialReview || status === SubmissionStatus.InReview;

  const isDigitalSigner = isReviewerASigner && submission?.signatureType === SignatureType.Digital;
  const inReviewMode =
    isInReviewPhase &&
    !isRejected &&
    !(isDigitalSigner && signingCompleted) &&
    (reviewState === SubmissionReviewState.SIGNER_CAN_SIGN ||
      reviewState === SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS ||
      (reviewState === SubmissionReviewState.REVIEWER_CAN_REVIEW && !isDigitalSigner));

  // A comment asking for changes doesn't move the submission — only approving
  // or sending it back does, so leaving mid-turn is worth confirming.
  const reviewTurnUnfinished =
    inReviewMode &&
    (reviewState === SubmissionReviewState.REVIEWER_CAN_REVIEW ||
      reviewState === SubmissionReviewState.SIGNER_CAN_SIGN);

  const hasOtherPendingReviewers = persistedReviewers.some(
    (r) => !r.isSigner && r.user.id !== user?.id && !r.completedAt && !r.reviewerStatus
  );
  const hasCustomerReviewers = persistedReviewers.some((r) => !r.isSigner && !r.isStaff);

  // Read-only reviewer/signer/attachment displays for the review surface.
  // Customers don't see Encamp's internal reviewers.
  const { signer: persistedSigner, reviewers: persistedNonSignerReviewers } = useMemo(
    () => partitionReviewersAndSigners(persistedReviewers),
    [persistedReviewers]
  );
  const attachmentDocuments = useMemo(
    () => documents.filter((d) => d.type === SubmissionDocumentType.Attachment),
    [documents]
  );
  // Signed first, rejected last, so a superseded document is distinguishable.
  const submissionDocumentRows = useMemo(
    () =>
      sortSubmissionDocumentsByType(
        documents.filter((d) => d.type !== SubmissionDocumentType.Attachment).map(toDisplayDocument)
      ),
    [documents]
  );
  const attachmentRowsToDisplay = useMemo(
    () => attachmentDocuments.map(toDisplayDocument),
    [attachmentDocuments]
  );

  // The preview combines the review turn with the persisted preparation state
  // before exposing the signing action.
  const signingUrl = signingUrlData?.getEmbeddedDocumentSigningUrl ?? undefined;

  const isSigningTurn =
    reviewState === SubmissionReviewState.SIGNER_CAN_SIGN ||
    reviewState === SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS;

  let title = "Review & Sign";
  let description = isStaff
    ? "Reviewers and the signer act on the document below; track progress here. Reviewers and the signer are assigned on the Reviewer Preparation step."
    : "Review and sign this submission when it's your turn.";
  if (inReviewMode) {
    title = isSigningTurn ? "Sign Submission" : "Review Submission";
    description = isSigningTurn
      ? "Read the document below, then add your signature to complete approval. If it needs changes, send it back for edits."
      : "Read the document below, then approve it. If it needs changes, send it back for edits.";
  }

  return (
    <Stack spacing={3}>
      <StepHeader
        title={title}
        description={description}
        titleAdornment={
          isStaff ? (
            <SubmissionStatusChipWithPopover
              submissionId={submissionId}
              submissionStatus={status}
              encampStatus={submission?.encampStatus ?? null}
              isStaff={isStaff}
              asStatusChip
            />
          ) : undefined
        }
      />

      <DetailsCard hideEditAction />

      <SubmissionRevisionBanner
        tenantId={tenantId}
        revisedFromSubmission={submission?.revisedFromSubmission}
        revisionSubmissions={submission?.revisionSubmissions}
      />

      <DocumentStepStatusBanner step={SubmissionStepType.ReviewAndSign} />

      <SignaturePreparationBanner
        submission={{
          status,
          signatureType: submission?.signatureType,
          documents: documents.map((d) => ({
            type: d.type,
            externalDocumentId: d.externalDocumentId,
          })),
          reviewers: persistedReviewers.map((r) => ({
            isSigner: r.isSigner,
            signatureStatus: r.signatureStatus,
          })),
        }}
      />

      {canClaimPeerReview && (
        <Alert
          severity="info"
          icon={<PersonAdd />}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={claiming}
              onClick={handleClaimPeerReview}
            >
              {claiming ? "Assigning..." : "Assign to Me"}
            </Button>
          }
        >
          This submission is in <strong>Initial Review</strong> with no peer reviewer assigned.
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Submission Details above owns everything about the submission itself;
            this summary is only the people and documents acting on it. */}
        <SubmissionReadOnlySummary
          tenantId={tenantId}
          isStaff={isStaff}
          facilities={[]}
          reviewers={persistedNonSignerReviewers}
          signer={persistedSigner}
          manifest={submission?.processedManifestExceptionReport}
          showTitle={false}
          showDueDate={false}
          showStaffAssignee={false}
          showNotes={false}
          showRecipient={false}
        />

        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Submission Document
          </Typography>
          {submissionDocumentRows.length > 0 ? (
            <DocumentUploadSection
              documents={submissionDocumentRows}
              onDeleteDocument={() => {}}
              canDeleteDocument={() => false}
              onSelectDocument={(doc) => setSelectedDocumentId(doc.id)}
              onOpenFullScreen={handleOpenFullScreen}
              tenantId={tenantId || undefined}
            />
          ) : (
            <Typography variant="body1" color="text.secondary">
              No submission documents
            </Typography>
          )}
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Additional Attachment(s)
          </Typography>
          {attachmentRowsToDisplay.length > 0 ? (
            <DocumentUploadSection
              documents={attachmentRowsToDisplay}
              onDeleteDocument={() => {}}
              canDeleteDocument={() => false}
              onSelectDocument={(doc) => setSelectedDocumentId(doc.id)}
              onOpenFullScreen={handleOpenFullScreen}
              tenantId={tenantId || undefined}
            />
          ) : (
            <Typography variant="body1" color="text.secondary">
              No additional attachments
            </Typography>
          )}
        </Box>
      </Stack>

      {isDigitalSigner && (
        <SignatureConfirmationAlert
          signingCompleted={signingCompleted}
          signingFlowConfirmed={signingFlowConfirmed}
          confirmationState={signatureConfirmationState}
          isAwaitingPostSignatureTransition={isAwaitingPostSignatureTransition}
          onRetry={retrySignatureConfirmation}
        />
      )}

      {selectedDocument && !documentUrl && !documentPreviewFailed && (
        <Skeleton
          data-testid="document-preview-loading"
          variant="rectangular"
          animation="wave"
          height={600}
          width="100%"
          sx={{ borderRadius: 0.5 }}
        />
      )}

      {selectedDocument && documentPreviewFailed && (
        <Box
          sx={{
            minHeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Unable to preview this document.
          </Typography>
        </Box>
      )}

      {documentUrl && (
        <SubmissionDocumentPreview
          previewDocumentUrl={documentUrl}
          fileExtension={selectedDocument?.document.fileExtension ?? undefined}
          reviewState={reviewState}
          signingCompleted={signingCompleted}
          signingPending={signingConfirmationPending}
          signatureType={submission?.signatureType}
          submissionStatus={submission?.status}
          isDocumentPreparedForSigning={isSelectedDocumentPreparedForSigning}
          isSigner={isReviewerASigner}
          onSigningDialogOpen={() => setSigningDialogOpen(true)}
        />
      )}

      {inReviewMode && (
        <SubmissionDisplayReviewOrSignDocument
          isReviewerASigner={isReviewerASigner}
          isCurrentUserStaff={isStaff}
          hasSigner={!!persistedSigner}
          hasCustomerReviewers={hasCustomerReviewers}
          hasOtherPendingReviewers={hasOtherPendingReviewers}
          reviewState={reviewState}
          reviewFinished={reviewFinished}
          rejectionComment={rejectionComment}
          submissionDocumentReviewId={currentUsersSubmissionDocumentReviewId}
          submissionStatus={submission?.status}
          signatureType={submission?.signatureType}
          signingCompleted={signingCompleted}
          isMutating={approving || rejecting || formState.isSubmitting}
          control={control}
          errors={formState.errors}
          onSubmit={handleSubmit(handleReviewSubmit)}
        />
      )}

      {signingDialogOpen && signingUrl && (
        <EmbeddedDocumentSigning
          open={signingDialogOpen}
          onClose={() => setSigningDialogOpen(false)}
          signingUrl={signingUrl}
          onSigned={markSignatureSubmitted}
        />
      )}

      {fullScreenDocument && fullScreenUrl && (
        <FullScreenDocumentPreview
          open={fullScreenPreviewOpen}
          onClose={() => setFullScreenPreviewOpen(false)}
          fileUrl={fullScreenUrl}
          fileExtension={fullScreenDocument.fileExtension ?? undefined}
          title={fullScreenDocument.name}
        />
      )}

      <UnfinishedChangesPrompt
        when={
          reviewTurnUnfinished &&
          !approving &&
          !rejecting &&
          !formState.isSubmitting &&
          !signingDialogOpen
        }
        header="You haven't completed your review"
        info="Approve this submission or send it back for edits before you leave. A comment on its own doesn't move it forward."
        confirmText="Leave without reviewing"
      />

      {/* Reviewers acting on the document get their actions from the review
          form; only the staff tracking view needs the step walk-back. */}
      {!inReviewMode && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button variant="text" onClick={() => goToPreviousStep(SubmissionStepType.ReviewAndSign)}>
            Back
          </Button>
        </Box>
      )}
    </Stack>
  );
}
